import { useState, useEffect } from "react";
import { Plus, Pencil, Trash2, Loader2, Users, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AdminLayout } from "@/components/AdminLayout";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { workshopsAPI } from "@/lib/api";
import { toast } from "sonner";
import { format, parse } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";

const timeSlots = [
    "01:00", "02:00", "03:00", "04:00", "05:00", "06:00", "07:00",
    "08:00", "09:00", "10:00", "11:00", "13:00", "14:00", "15:00",
    "16:00", "17:00", "18:00", "19:00", "20:00", "21:00", "22:00",
    "23:00", "00:00"
];

export default function WorkshopsPage() {
  const [workshops, setWorkshops] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedWorkshop, setSelectedWorkshop] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [workshopDate, setWorkshopDate] = useState(null);
  const [formData, setFormData] = useState({
    workshop_type: "evento",
    date: "",
    time: "",
    max_slots: 10,
  });

  useEffect(() => {
    loadWorkshops();
  }, []);

  const loadWorkshops = async () => {
    try {
      const response = await workshopsAPI.list();
      setWorkshops(response.data);
    } catch (error) {
      toast.error("Erro ao carregar datas do evento");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDialog = (workshop = null) => {
    if (workshop) {
      setSelectedWorkshop(workshop);
      setFormData({
        workshop_type: workshop.workshop_type || "evento",
        date: workshop.date,
        time: workshop.time,
        max_slots: workshop.max_slots,
      });
      setWorkshopDate(parse(workshop.date, "yyyy-MM-dd", new Date()));
    } else {
      setSelectedWorkshop(null);
      setFormData({
        workshop_type: "evento",
        date: "",
        time: "",
        max_slots: 10,
      });
      setWorkshopDate(null);
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedWorkshop(null);
    setWorkshopDate(null);
  };

  const handleDateSelect = (date) => {
    setWorkshopDate(date);
    if (date) {
      setFormData({ ...formData, date: format(date, "yyyy-MM-dd") });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      if (selectedWorkshop) {
        await workshopsAPI.update(selectedWorkshop.id, formData);
        toast.success("Data atualizada com sucesso!");
      } else {
        await workshopsAPI.create(formData);
        toast.success("Data criada com sucesso!");
      }
      handleCloseDialog();
      loadWorkshops();
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao salvar";
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedWorkshop) return;

    try {
      await workshopsAPI.delete(selectedWorkshop.id);
      toast.success("Data removida com sucesso!");
      setIsDeleteDialogOpen(false);
      setSelectedWorkshop(null);
      loadWorkshops();
    } catch (error) {
      toast.error("Erro ao remover data");
    }
  };

  const openDeleteDialog = (workshop) => {
    setSelectedWorkshop(workshop);
    setIsDeleteDialogOpen(true);
  };

  const formatDate = (dateStr) => {
    return format(parse(dateStr, "yyyy-MM-dd", new Date()), "dd/MM/yyyy", { locale: ptBR });
  };

  const WorkshopCard = ({ workshop }) => {
    const isFull = workshop.available_slots <= 0;

    return (
      <Card className={cn(
        "workshop-card border-0 shadow-md overflow-hidden",
        isFull && "opacity-60"
      )} data-testid={`workshop-card-${workshop.id}`}>
        <div className="h-2 bg-primary" />
        <CardContent className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-primary/10">
                <Star className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="font-nunito font-bold text-lg">
                  {formatDate(workshop.date)}
                </p>
                <p className="text-muted-foreground">{workshop.time}</p>
              </div>
            </div>
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleOpenDialog(workshop)}
                className="rounded-full"
                data-testid={`edit-workshop-${workshop.id}`}
              >
                <Pencil className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => openDeleteDialog(workshop)}
                className="rounded-full text-destructive hover:text-destructive"
                data-testid={`delete-workshop-${workshop.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm">
                {workshop.booked_slots}/{workshop.max_slots} vagas
              </span>
            </div>
            <span className={cn(
              "text-xs px-3 py-1 rounded-full font-medium",
              isFull ? "status-full" : "status-available"
            )}>
              {isFull ? "Esgotado" : `${workshop.available_slots} disponíveis`}
            </span>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <AdminLayout title="Gerenciar Datas do Evento">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
          <h2 className="text-xl font-bold font-nunito">Datas Disponíveis</h2>
          <Button
            onClick={() => handleOpenDialog()}
            className="rounded-full btn-animate"
            data-testid="add-workshop-button"
          >
            <Plus className="w-5 h-5 mr-2" />
            Nova Data
          </Button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : workshops.length === 0 ? (
          <Card className="border-dashed border-2">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <CalendarIcon className="w-12 h-12 mb-4 text-primary/40" />
              <p className="text-muted-foreground text-center">
                Nenhuma data cadastrada para o evento.
              </p>
              <Button
                variant="outline"
                className="mt-4 rounded-full"
                onClick={() => handleOpenDialog()}
              >
                <Plus className="w-4 h-4 mr-2" />
                Adicionar Data
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {workshops.map((workshop) => (
              <WorkshopCard key={workshop.id} workshop={workshop} />
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-nunito text-xl">
              {selectedWorkshop ? "Editar Data" : "Cadastrar Nova Data"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Data *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full h-11 rounded-xl justify-start text-left font-normal",
                        !workshopDate && "text-muted-foreground"
                      )}
                      data-testid="workshop-date-button"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {workshopDate ? format(workshopDate, "dd/MM/yyyy") : "Selecione a data"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={workshopDate}
                      onSelect={handleDateSelect}
                      disabled={(date) => date < new Date()}
                      initialFocus
                      locale={ptBR}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label>Horário *</Label>
                <Select
                  value={formData.time}
                  onValueChange={(value) => setFormData({ ...formData, time: value })}
                >
                  <SelectTrigger className="h-11 rounded-xl" data-testid="workshop-time-select">
                    <SelectValue placeholder="Selecione o horário" />
                  </SelectTrigger>
                  <SelectContent>
                    {timeSlots.map((time) => (
                      <SelectItem key={time} value={time}>{time}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="max_slots">Número de Vagas</Label>
                <Input
                  id="max_slots"
                  type="number"
                  min="1"
                  max="50"
                  value={formData.max_slots}
                  onChange={(e) => setFormData({ ...formData, max_slots: parseInt(e.target.value) || 10 })}
                  className="h-11 rounded-xl"
                  data-testid="workshop-slots-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog} className="rounded-full">
                Cancelar
              </Button>
              <Button type="submit" disabled={isSaving} className="rounded-full" data-testid="save-workshop-button">
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : selectedWorkshop ? "Salvar" : "Criar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-nunito">Confirmar Exclusão</DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground">
            Tem certeza que deseja remover esta data? Todos os agendamentos relacionados também serão removidos.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)} className="rounded-full">
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDelete} className="rounded-full" data-testid="confirm-delete-workshop">
              Remover
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
